import { Injectable } from '@angular/core';
import { ApiService } from '@client/app/services/api/api-service/api.service';
import { SessionService } from '@monsido/core/session/session.service';
import { RequestParamsType } from '@monsido/ng2/external/http/options';
import { AccessibilityCheckModel } from '@client/app/models/accessibility/accessibility-check.model';
import { AccessibilityFastTrackModel } from '@client/app/models/accessibility/fast-track.model';
import { BaseRepo, BaseRepoRequestType } from '../../base-repo/base-repo';

type AccessibilityFastTrackRepoRequestType = RequestParamsType & BaseRepoRequestType;

@Injectable({
    providedIn: 'root',
})
export class AccessibilityFastTrackRepoService extends BaseRepo {
    constructor (private apiService: ApiService, protected sessionService: SessionService) {
        super();
    }

    async getAll (params?: AccessibilityFastTrackRepoRequestType, config?: any): Promise<AccessibilityFastTrackModel[]> {
        config = config || {};
        params = params || {};
        params = this.setGroupId(params);

        const models = await this.apiService.get<any[]>(
            `domains/${this.getDomainId()}/accessibility/fast_track`,
            params,
            config,
        );

        return this.toFastTrackModels(models);
    }

    async get (id: string | number, params?: AccessibilityFastTrackRepoRequestType, config?: any): Promise<AccessibilityFastTrackModel> {
        config = config || {};
        params = params || {};
        params = this.setGroupId(params);

        const model = await this.apiService.get<any>(
            `domains/${this.getDomainId()}/accessibility/fast_track/${id}`,
            params,
            config,
        );

        return this.toFastTrackModel(model);
    }

    async getAllChecks (id: string | number, params?: AccessibilityFastTrackRepoRequestType, config?: any): Promise<AccessibilityCheckModel[]> {
        config = config || {};
        params = params || {};
        params = this.setGroupId(params);

        const models = await this.apiService.get<any[]>(
            `domains/${this.getDomainId()}/accessibility/fast_track/${id}/checks`,
            params,
            config,
        );

        return this.toModels(models);
    }

    async updateCheck (
        accId: string | number,
        check: AccessibilityCheckModel,
        params?: AccessibilityFastTrackRepoRequestType,
    ): Promise<AccessibilityCheckModel> {
        params = params || {};
        params = this.setGroupId(params);

        const model = await this.apiService.patch<any>(
            `domains/${this.getDomainId()}/accessibility/fast_track/${accId}/checks/${check.id}`,
            check,
            params,
        );

        return this.toModel(model);
    }

    async getAllPages (
        accId: string | number,
        checkId: string | number,
        params?: AccessibilityFastTrackRepoRequestType,
        config?: any,
    ): Promise<any> {
        config = config || {};
        params = params || {};
        params = this.setGroupId(params);

        return this.apiService.get(
            `domains/${this.getDomainId()}/accessibility/fast_track/${accId}/checks/${checkId}/pages`,
            params,
            config,
        );
    }

    async closeCaption (
        accId: string | number,
        data?: any,
        params?: AccessibilityFastTrackRepoRequestType,
        config?: any,
    ): Promise<any> {
        config = config || {};
        data = data || {};
        params = params || {};
        params = this.setGroupId(params);

        return this.apiService.post(
            `domains/${this.getDomainId()}/accessibility/fast_track/${accId}/closed_caption`,
            data,
            undefined,
            params,
        );
    }

    async undoAccessibilityCheck (
        domainId: string | number,
        accessibilitySourceCodeId: string | number,
        accessibilityCheckId: string | number,
        params?: any,
    ): Promise<any> {
        params = params || {};

        return this.apiService.patch(
            `domains/${domainId}/accessibility/fast_track/${accessibilitySourceCodeId}/checks/${accessibilityCheckId}`,
            params,
        );
    }

    async markCheckAsFixed (
        domainId: string | number,
        sourceCodeId: string | number,
        checkId: string | number,
        params?: AccessibilityFastTrackRepoRequestType,
    ): Promise<any> {
        params = params || {};
        params = this.setGroupId(params);

        return this.apiService.delete(
            `domains/${domainId}/accessibility/fast_track/${sourceCodeId}/checks/${checkId}/pages`,
            params,
        );
    }

    private toModels (models: any[]): AccessibilityCheckModel[] {
        return models.map(model => this.toModel(model));
    }

    private toModel (model: any): AccessibilityCheckModel {
        return new AccessibilityCheckModel(model);
    }

    private toFastTrackModels (models: any[]): AccessibilityFastTrackModel[] {
        return models.map(model => this.toFastTrackModel(model));
    }

    private toFastTrackModel (model: any): AccessibilityFastTrackModel {
        return new AccessibilityFastTrackModel(model);
    }
}
